import { useState, useEffect, useContext } from "react";
import { brandMappingV3 } from "~src/api/brandMapping";
import { BrandMappingContextV3 } from "~src/contexts/BrandMappingContextV3";
import { FullBrand } from "~src/types/brand";
import { GeographicalSummaryData, MapOfBrands } from "~src/types/brandMapping";
import { aggregateCountries, useExtractCountries, filterMapOfBrands } from "./utils";

export type FullBrandLoadContext = {
  fullBrand: FullBrand | null;
  loading: boolean;
  error: string | null;
};

export type BrandMappingFilters = {
  brand_country?: string[];
  ad_country?: string[];
  product_price_range?: string;
  estimated_monthly_traffic?: string;
  estimated_monthly_revenue?: string;
  social_followers?: string;
  has_running_ads?: boolean;
  selectedCategories?: number[];
};

const useBrandMappingV3 = (
  searchText: string | undefined,
  exactMatch: boolean,
  brandToRemove: MapOfBrands[] | undefined,
) => {
  const [loadingV3, setLoading] = useState<boolean>(false);
  const [initialLoadingV3, setInitialLoading] = useState<boolean>(false);
  const [geographicalSummaryV3, setGeographicalSummary] = useState<GeographicalSummaryData>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [errorV3, setError] = useState<string | null>(null);
  const [filtersV3, setFiltersV3] = useState<BrandMappingFilters>();

  const {
    mapOfBrandsContext,
    setMapOfBrandsContext,
    mapOfBrandsFullResponseContext,
    setMapOfBrandsFullResponseContext,
    setMappedProductCategoryIds,
  } = useContext(BrandMappingContextV3) || {};

  const [mappedBrandsV3, setMappedBrands] = useState<MapOfBrands[] | undefined>(
    mapOfBrandsContext
  );

  const itemsPerPage = 20;

  useEffect(() => {
    if (!searchText) return;

    const extractCategoryIds = (data: MapOfBrands[]) => {
      // Extract all category_id values from products
      const categoryIds = data.flatMap((item) =>
        item.products.map((product) => product.category_id)
      );
      // Filter out null values and get distinct IDs
      const distinctCategoryIds = [...new Set(categoryIds)].filter(
        (id) => id !== null
      );
      return distinctCategoryIds.length > 0 ? distinctCategoryIds : [];
    };

    const fetchMapping = async () => {
      try {
        setInitialLoading(true);
        setLoading(true);
        setError(null);
        const modifiedSearchString = exactMatch
          ? `"${searchText}"`
          : searchText;

        const response = await brandMappingV3(
          modifiedSearchString,
          1,
          brandToRemove?.map((brand) => brand.brand.brand_id)
        );

        if (setMapOfBrandsFullResponseContext) {
          setMapOfBrandsFullResponseContext(response.data);
        }

        if (setMappedProductCategoryIds) {
          setMappedProductCategoryIds(extractCategoryIds(response.data));
        }

        let mapping = Array.isArray(response.data) ? response.data : [];
        mapping = filterMapOfBrands(mapping, filtersV3);
        const geographicalSummary = aggregateCountries(mapping);

        setMappedBrands(mapping);
        setGeographicalSummary(geographicalSummary);
        setCurrentPage(1);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchMapping();
  }, [
    searchText,
    exactMatch,
    brandToRemove,
    filtersV3,
    setMapOfBrandsFullResponseContext,
    setMappedProductCategoryIds,
  ]);

  useEffect(() => {
    if (
      mapOfBrandsFullResponseContext &&
      mapOfBrandsFullResponseContext.length > 0
    ) {
      setInitialLoading(true);
      const filteredMapping = filterMapOfBrands(
        mapOfBrandsFullResponseContext,
        filtersV3
      );
      const geographicalSummary = aggregateCountries(filteredMapping);

      setMappedBrands(filteredMapping);
      setGeographicalSummary(geographicalSummary);
      setCurrentPage(1);
      setInitialLoading(false);
    }
  }, [mapOfBrandsFullResponseContext, filtersV3]);

  useEffect(() => {
    if (!setMapOfBrandsContext) return; // Ensure context setter is available

    // Calculate paginated brands
    const paginatedBrands = Array.isArray(mappedBrandsV3)
      ? mappedBrandsV3.slice(0, currentPage * itemsPerPage)
      : [];

    // Update context with paginated brands
    setMapOfBrandsContext(paginatedBrands);
  }, [mappedBrandsV3, currentPage, itemsPerPage, setMapOfBrandsContext]);

  const loadMoreV3 = () => {
    if (mappedBrandsV3 && currentPage * itemsPerPage >= mappedBrandsV3.length)
      return;
    setCurrentPage((prev) => prev + 1);
  };

  return {
    loadMoreV3,
    geographicalSummaryV3,
    filtersV3,
    setFiltersV3,
    loadingV3,
    initialLoadingV3,
    errorV3,
    brandCountriesV3: useExtractCountries().brandCountries,
  };
};

export default useBrandMappingV3;
